import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { APP_ICON_API_SERVICE_TOKEN, PERSON_API_SERVICE_TOKEN } from '@nk/api-lib';
import { AppIconEntity, PersonFlagEntity, PersonFlagTypeEntity, WithUuid } from '@nk/data-entities-lib';
import { FlagIconComponent, SpinnerComponent } from '@nk/ui-lib';
import { arrayToDict, filterTruthyValues } from '@nk/utils-lib';
import { combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'nk-person-flag-icon',
  imports: [AsyncPipe, TranslateModule, SpinnerComponent, FlagIconComponent],
  templateUrl: './person-flag-icon.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PersonFlagIconComponent {
  // Signal inputs
  public personFlagEntity = input<PersonFlagEntity | undefined>();
  public personFlagTypeEntity = input<PersonFlagTypeEntity | undefined>(); // if not provided the component will fetch it
  /* one of lg, 2x, 3x etc.. See fontawesome documentation (fa- will be prefixed) */
  public size = input<string>('');
  public andClass = input<string>('');
  public colorClassOverride = input<string>('');
  public verbose = input<boolean>(false);

  private readonly _defaultFlag: WithUuid<AppIconEntity> = {
    uuid: 'flag_not_found',
    app_icon_key: 'flag_not_found',
    icon_primary: 'fa-flag',
    icon_secondary: '',
    icon_emphasis: '',
  };

  private readonly _appIconService = inject(APP_ICON_API_SERVICE_TOKEN);
  private readonly _personService = inject(PERSON_API_SERVICE_TOKEN);

  // Computed signals for reactive data
  public readonly personFlagType$ = computed(() => {
    const personFlagTypeEntity = this.personFlagTypeEntity();
    const personFlagEntity = this.personFlagEntity();

    if (personFlagTypeEntity) {
      return of(personFlagTypeEntity);
    }

    if (!personFlagEntity) {
      return of(null);
    }

    const personFlagTypeDict$ = this._personService.personFlagTypeListCache
      .get()
      .pipe(map((personFlagType) => arrayToDict(personFlagType, (a) => a.uuid || '')));

    return combineLatest([of(personFlagEntity), personFlagTypeDict$]).pipe(
      filterTruthyValues(),
      map(([personFlag, personFlagTypeDict]) => personFlagTypeDict[personFlag.person_flag_type_uuid] || null),
    );
  });

  public readonly currentIcon$ = computed(() => {
    const personFlagTypeEntity = this.personFlagTypeEntity();
    const personFlagType$ = this.personFlagType$();

    if (personFlagTypeEntity) {
      return this._appIconService.appIconDictCache.getStableCache().pipe(
        map((personFlagIconDict) => {
          const appIconUuid = personFlagTypeEntity.app_icon_uuid;
          if (!personFlagIconDict[appIconUuid]) {
            console.warn('Missing person flag icons for ' + personFlagTypeEntity.person_flag_type_key);
          }
          return appIconUuid ? personFlagIconDict[appIconUuid] : this._defaultFlag;
        }),
      );
    }

    if (!personFlagType$) {
      return of(null);
    }

    return combineLatest([personFlagType$, this._appIconService.appIconDictCache.getStableCache()]).pipe(
      filterTruthyValues(),
      map(([personFlagType, personFlagIconDict]) => {
        if (!personFlagType) return null;
        const appIconUuid = personFlagType.app_icon_uuid;
        return appIconUuid ? personFlagIconDict && personFlagIconDict[appIconUuid] : null;
      }),
    );
  });
}
